'use client';

import { useState, useEffect, useRef } from 'react';
import { Bookmark, BookmarkCheck, Lightbulb, X, Eye, CheckCircle2, XCircle } from 'lucide-react';
import Link from '@/components/AppLink';
import PracticeImage from '@/components/PracticeImage';
import axiosInstance from '@/utils/axiosInstance';
import { getCachedAIAnswer, setCachedAIAnswer } from '@/utils/aiAnswerCache';

interface Question {
  _id: string;
  question: string;
  answer: string;
  explanation?: string;
  question_image_url?: string;
}

interface Option {
  _id: string;
  option_text: string;
  option_type: string;
  path_url?: string;
}

interface QuestionItem {
  question: Question;
  options: Option[];
}

interface QuestionsClientProps {
  questions: QuestionItem[];
  apiUrl: string;
  topicName: string;
  subTopicName: string;
  exam?: string;
  currentPage: number;
  totalPages: number;
  totalQuestions: number;
}

function normalizePracticeHtml(html: string) {
  return html.replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ');
}

export default function QuestionsClient({ questions, apiUrl, topicName, subTopicName, exam, currentPage, totalPages, totalQuestions }: QuestionsClientProps) {
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
  const [savedQuestions, setSavedQuestions] = useState<Set<string>>(new Set());
  const [isMounted, setIsMounted] = useState(false);
  const trackingRef = useRef(false);
  
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [currentQuestionForExplanation, setCurrentQuestionForExplanation] = useState<QuestionItem | null>(null);
  
  const [showAIExplanationModal, setShowAIExplanationModal] = useState(false);
  const [aiExplanation, setAiExplanation] = useState('');
  const [isGeneratingAIExplanation, setIsGeneratingAIExplanation] = useState(false);
  const [aiExplanationError, setAiExplanationError] = useState<string | null>(null);
  const [currentQuestionForAI, setCurrentQuestionForAI] = useState<QuestionItem | null>(null);
  
  const modalRef = useRef<HTMLDivElement>(null);
  const explanationModalRef = useRef<HTMLDivElement>(null);
  

  useEffect(() => {
    setIsMounted(true);
    loadSavedQuestions();
    if (!trackingRef.current) {
      trackingRef.current = true;
      trackTopicVisit();
    }
  }, []);

  const trackTopicVisit = async () => {
    try {
      await axiosInstance.post('/api/student/track-topic-visit', {
        topicName,
        subTopicName
      });
    } catch {
      // Not logged in or error, skip tracking
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (showAIExplanationModal && modalRef.current && !modalRef.current.contains(target)) {
        setShowAIExplanationModal(false);
      }
      if (showExplanationModal && explanationModalRef.current && !explanationModalRef.current.contains(target)) {
        setShowExplanationModal(false);
      }
    };

    if (showAIExplanationModal || showExplanationModal) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [showAIExplanationModal, showExplanationModal]);

  const loadSavedQuestions = () => {
    try {
      const saved = localStorage.getItem('savedQuestions');
      if (saved) setSavedQuestions(new Set(JSON.parse(saved)));
    } catch {
      // Ignore localStorage errors
    }
  };

  const toggleSaveQuestion = async (questionId: string) => {
    const newSavedQuestions = new Set(savedQuestions);
    const isCurrentlySaved = savedQuestions.has(questionId);
    
    if (isCurrentlySaved) {
      newSavedQuestions.delete(questionId);
    } else {
      newSavedQuestions.add(questionId);
    }
    setSavedQuestions(newSavedQuestions);
    
    try {
      localStorage.setItem('savedQuestions', JSON.stringify([...newSavedQuestions]));
      if (isCurrentlySaved) {
        await axiosInstance.post('/api/student/remove-saved-question', { questionId });
      } else {
        await axiosInstance.post('/api/student/save-question', { questionId });
      }
    } catch {
      // Continue with localStorage only if API fails
    }
  };

  const handleAnswerSelect = (questionId: string, optionId: string) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: optionId,
    }));
  };

  const toggleExplanation = (questionItem: QuestionItem) => {
    setCurrentQuestionForExplanation(questionItem);
    setShowExplanationModal(true);
  };

  const getCorrectAnswerOption = (questionItem: QuestionItem) => {
    return questionItem.options.find(
      (option) =>
        option.option_text === questionItem.question.answer ||
        option.option_text.toLowerCase() === questionItem.question.answer.toLowerCase(),
    );
  };

  const getOptionStyling = (questionItem: QuestionItem, option: Option) => {
    const isSelected = selectedAnswers[questionItem.question._id] === option._id;
    const correctOption = getCorrectAnswerOption(questionItem);
    const isCorrect = correctOption && correctOption._id === option._id;

    if (isSelected) {
      if (isCorrect) {
        return 'bg-emerald-50 border-emerald-500 border-2';
      } else {
        return 'bg-amber-50 border-amber-500 border-2';
      }
    }
    return 'practice-option-idle';
  };

  const formatAIResponse = (text: string) => {
    return `<p>${text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>')
      .replace(/<p><\/p>/g, '')
      .replace(/<p><br><\/p>/g, '')}</p>`;
  };

  const handleAIExplanation = async (questionItem: QuestionItem) => {
    setCurrentQuestionForAI(questionItem);
    setShowAIExplanationModal(true);
    setAiExplanationError(null);
    
    const questionId = questionItem.question._id;
    const cachedAnswer = getCachedAIAnswer(questionId);
    
    if (cachedAnswer) {
      setAiExplanation(cachedAnswer);
      setIsGeneratingAIExplanation(false);
      return;
    }

    setIsGeneratingAIExplanation(true);
    setAiExplanation('');

    try {
      const apiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY || '';
      if (!apiKey) {
        throw new Error('Google API Key is not configured.');
      }

      const { GoogleGenerativeAI } = await import('@google/generative-ai');
      const genAI = new GoogleGenerativeAI(apiKey);
      const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });
      const prompt = `You are an expert educator. Explain this question clearly and simply.

**QUESTION:**
${questionItem.question.question}

**INSTRUCTIONS:**
1. Explain the underlying concept in simple terms
2. Walk through the reasoning step by step
3. End with: "The correct answer is: [letter]"
4. Keep explanation under 200 words
5. Use plain text only - no formatting, headings, or bullet points
6. Write in a conversational tone

Focus only on helping students understand the concept and reasoning.`;
      
      const result = await model.generateContent(prompt);
      const response = await result.response;
      const formattedAnswer = formatAIResponse(response.text());
      setAiExplanation(formattedAnswer);
      setCachedAIAnswer(questionId, formattedAnswer);
    } catch (err) {
      console.error('AI explanation error:', err);
      setAiExplanationError('Failed to generate AI explanation. Try Again');
    } finally {
      setIsGeneratingAIExplanation(false);
    }
  };

  const closeAIModal = () => {
    setShowAIExplanationModal(false);
  };

  const closeExplanationModal = () => {
    setShowExplanationModal(false);
  };

  const indexOfFirstQuestion = (currentPage - 1) * 10;
  const currentQuestions = questions;

  const paginate = (pageNumber: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPaginationUrl = (page: number) => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    if (exam) params.set('exam', exam);
    return `/practice/${topicName}/${subTopicName}${params.toString() ? `?${params.toString()}` : ''}`;
  };

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .questions-rich-text,
          .questions-rich-text p,
          .questions-rich-text li,
          .questions-rich-text h1,
          .questions-rich-text h2,
          .questions-rich-text h3,
          .questions-rich-text div,
          .questions-rich-text span,
          .questions-rich-text strong,
          .questions-rich-text a {
            word-break: normal;
            overflow-wrap: break-word;
            white-space: normal;
            hyphens: none;
          }

          .questions-rich-text pre,
          .questions-rich-text code {
            word-break: normal;
            overflow-wrap: normal;
            white-space: pre-wrap;
          }
        `,
        }}
      />
      <div className="space-y-4">
        {currentQuestions.map((item, index) => {
          const questionNumber = indexOfFirstQuestion + index + 1;
          return (
            <div
              key={item.question._id}
              className="practice-question-card rounded-xl transition duration-300"
            >
              <div className="p-4 sm:p-5">
                <div className="mb-4">
                  <div className="practice-question-header rounded-t-xl p-2 sm:p-3 flex justify-between items-center gap-2 sm:gap-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-[var(--study-primary)] rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm sm:text-base">{questionNumber}</span>
                      </div>
                      <div className="study-title text-sm sm:text-base font-medium whitespace-nowrap">
                        Question {questionNumber} of {totalQuestions}
                      </div>
                    </div>
                    <button
                      onClick={() => toggleSaveQuestion(item.question._id)}
                      className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-2 rounded-md sm:rounded-lg transition duration-300 cursor-pointer transform hover:scale-105 ${
                        isMounted && savedQuestions.has(item.question._id)
                          ? 'study-button-primary text-white'
                          : 'study-badge-soft hover:text-[var(--study-primary)]'
                      }`}
                      aria-label={isMounted && savedQuestions.has(item.question._id) ? 'Remove question from saved' : 'Save question for later'}
                    >
                      {isMounted && savedQuestions.has(item.question._id) ? (
                        <BookmarkCheck className="h-3 w-3 sm:h-4 sm:w-4" aria-hidden="true" />
                      ) : (
                        <Bookmark className="h-3 w-3 sm:h-4 sm:w-4" aria-hidden="true" />
                      )}
                      <span className="text-sm font-medium">
                        {isMounted && savedQuestions.has(item.question._id) ? 'Saved' : 'Save'}
                      </span>
                    </button>
                  </div>
                  
                  <div
 
                    className="practice-content practice-question-body text-base sm:text-lg leading-relaxed p-4 rounded-b-xl"
 
                    dangerouslySetInnerHTML={{ __html: normalizePracticeHtml(item.question.question) }}
                  />
                  {item.question.question_image_url && (
                    <div className="mt-4">
                      <PracticeImage src={`${apiUrl}/uploads/${item.question.question_image_url}`} alt="Question" />
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  {item.options.map((option) => {
                    const isSelected = selectedAnswers[item.question._id] === option._id;
                    const correctOption = getCorrectAnswerOption(item);
                    const isCorrect = correctOption && correctOption._id === option._id;
                    const showResult = isSelected && (isCorrect || !isCorrect);
                    
                    return (
                      <label
                        key={option._id}
                        className={`flex items-start p-2.5 border rounded-lg cursor-pointer transition-[color,background-color,border-color,opacity,transform] duration-200 ${getOptionStyling(item, option)}`}
                      >
                        <input
                          type="radio"
                          name={`question-${item.question._id}`}
                          value={option._id}
                          checked={isSelected}
                          onChange={() => handleAnswerSelect(item.question._id, option._id)}
                          className={`h-4 w-4 mt-1 mr-3 cursor-pointer ${
                            isSelected && isCorrect
                              ? 'text-emerald-500 border-emerald-500 focus:ring-emerald-500'
                              : isSelected && !isCorrect
                              ? 'text-amber-500 border-amber-500 focus:ring-amber-500'
                              : 'text-[var(--study-primary)] border-[var(--study-border)] focus:ring-[var(--study-primary)]'
                          }`}
                          aria-label={`Select option ${option._id} for question ${questionNumber}`}
                        />
                        <div className="flex-1 flex items-start justify-between gap-2">
                          <div className="flex-1">
                            {option.option_type === 'text' ? (
                                 <div
                                className={`practice-content ${
 
                                  isSelected && isCorrect
                                    ? 'text-gray-800'
                                    : isSelected && !isCorrect
                                    ? 'text-gray-800'
                                    : 'study-muted'
                                }`}
                                dangerouslySetInnerHTML={{ __html: normalizePracticeHtml(option.option_text) }}
                              />
                            ) : (
                              <div>
                                {option.option_text && (
                                  <p className={`font-normal mb-2 ${
                                    isSelected && isCorrect
                                      ? 'text-gray-800'
                                      : isSelected && !isCorrect
                                      ? 'text-gray-800'
                                      : 'study-muted'
                                  }`}>{option.option_text}</p>
                                )}
                                {option.path_url && (
                                  <PracticeImage src={`${apiUrl}/uploads/${option.path_url}`} alt="Option" compact />
                                )}
                              </div>
                            )}
                          </div>
                          {showResult && (
                            <div className="flex-shrink-0 mt-1">
                              {isCorrect ? (
                                <CheckCircle2 className="h-5 w-5 text-emerald-500" aria-hidden="true" />
                              ) : (
                                <XCircle className="h-5 w-5 text-amber-500" aria-hidden="true" />
                              )}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <button
                    onClick={() => toggleExplanation(item)}
                    className="study-button-primary group inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl transition-[color,background-color,border-color,opacity,transform] duration-300 font-bold cursor-pointer transform hover:scale-105 min-w-[140px]"
                    aria-label="View explanation for this question"
                  >
                    <Eye className="h-4 w-4 transition-transform group-hover:scale-110" aria-hidden="true" />
                    <span>Explanation</span>
                  </button>
                  <span className="practice-tooltip-wrap relative inline-flex">
                    <button
                      type="button"
                      disabled
                      className="study-filter-idle px-5 py-2.5 rounded-xl transition duration-300 font-bold flex items-center justify-center gap-2 cursor-not-allowed opacity-70"
                      aria-label="Owl AI explanation will come soon"
                    >
                      <Lightbulb className="h-4 w-4" aria-hidden="true" />
                      Owl AI Explanation
                    </button>
                    <span className="practice-tooltip" role="tooltip">Owl AI is coming soon</span>
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="flex justify-between items-center mt-6 mb-4">
        <Link
          href={currentPage > 1 ? getPaginationUrl(currentPage - 1) : '#'}
          onClick={() => currentPage > 1 && paginate(currentPage - 1)}
          className={`px-5 py-2.5 rounded-xl font-bold transition duration-300 transform hover:scale-105 cursor-pointer inline-block ${
            currentPage === 1
              ? 'study-filter-idle opacity-60 cursor-not-allowed hover:scale-100 pointer-events-none'
              : 'study-filter-idle'
          }`}
          aria-label={currentPage === 1 ? 'Previous page (disabled)' : `Go to page ${currentPage - 1}`}
          aria-disabled={currentPage === 1}
        >
          Previous
        </Link>
        <Link
          href={currentPage < totalPages ? getPaginationUrl(currentPage + 1) : '#'}
          onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
          className={`px-5 py-2.5 rounded-xl font-bold transition duration-300 transform hover:scale-105 cursor-pointer inline-block ${
            currentPage === totalPages
              ? 'study-filter-idle opacity-60 cursor-not-allowed hover:scale-100 pointer-events-none'
              : 'study-button-primary'
          }`}
          aria-label={currentPage === totalPages ? 'Next page (disabled)' : `Go to page ${currentPage + 1}`}
          aria-disabled={currentPage === totalPages}
        >
          Next
        </Link>
      </div>
      
      {totalPages > 1 && (
        <div className="flex justify-center">
          <div className="flex items-center space-x-2">
            <Link
              href={currentPage > 1 ? getPaginationUrl(currentPage - 1) : '#'}
              onClick={() => currentPage > 1 && paginate(currentPage - 1)}
              className={`p-2 rounded-lg cursor-pointer transform hover:scale-110 transition duration-200 inline-block ${
                currentPage === 1
                  ? 'study-muted cursor-not-allowed hover:scale-100 pointer-events-none opacity-60'
                  : 'study-link hover:bg-[var(--study-primary-soft)]'
              }`}
              aria-label={currentPage === 1 ? 'Previous page (disabled)' : `Go to page ${currentPage - 1}`}
              aria-disabled={currentPage === 1}
            >
              {'<'}
            </Link>
            {Array.from({ length: totalPages }).map((_, index) => {
              const pageNum = index + 1;
              return (
                <Link
                  key={index}
                  href={getPaginationUrl(pageNum)}
                  onClick={() => paginate(pageNum)}
                  className={`w-10 h-10 rounded-lg font-bold transition duration-300 cursor-pointer transform hover:scale-110 inline-flex items-center justify-center ${
                    currentPage === pageNum ? 'study-button-primary' : 'study-link hover:bg-[var(--study-primary-soft)]'
                  }`}
                  aria-label={`Go to page ${pageNum}`}
                  aria-current={currentPage === pageNum ? 'page' : undefined}
                >
                  {pageNum}
                </Link>
              );
            })}
            <Link
              href={currentPage < totalPages ? getPaginationUrl(currentPage + 1) : '#'}
              onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
              className={`p-2 rounded-lg cursor-pointer transform hover:scale-110 transition duration-200 inline-block ${
                currentPage === totalPages
                  ? 'study-muted cursor-not-allowed hover:scale-100 pointer-events-none opacity-60'
                  : 'study-link hover:bg-[var(--study-primary-soft)]'
              }`}
              aria-label={currentPage === totalPages ? 'Next page (disabled)' : `Go to page ${currentPage + 1}`}
              aria-disabled={currentPage === totalPages}
            >
              {'>'}
            </Link>
          </div>
        </div>
      )}

      {/* Normal Explanation Modal */}
      {showExplanationModal && currentQuestionForExplanation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="relative w-full h-full max-h-screen flex items-center justify-center p-4 sm:p-6">
            <div 
              ref={explanationModalRef}
              className="practice-modal relative w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl rounded-xl transition-[color,background-color,border-color,opacity,transform] duration-300 ease-out flex flex-col max-h-[90vh] sm:max-h-[85vh]"
            >
              <div className="flex items-center justify-between p-4 pb-3 border-b border-[var(--study-border)] flex-shrink-0">
                <h3 className="study-title text-lg font-bold flex items-center gap-2">
                  <Eye className="study-link h-5 w-5" />
                  Explanation
                </h3>
                <button
                  onClick={closeExplanationModal}
                  className="study-muted hover:text-[var(--study-primary)] transition-colors cursor-pointer transform hover:scale-110"
                  aria-label="Close explanation modal"
                >
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto px-4 py-3">
                <div className="space-y-3">
                  {currentQuestionForExplanation.question.explanation && (
                    <div className="practice-content text-base leading-relaxed">
                      <div
 
                        className="practice-content"
 
                        dangerouslySetInnerHTML={{ __html: normalizePracticeHtml(currentQuestionForExplanation.question.explanation) }}
                      />
                    </div>
                  )}

                  {!currentQuestionForExplanation.question.explanation && (
                    <div className="study-muted text-center py-3">
                      <p>No explanation available for this question.</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="p-4 pt-3 border-t border-[var(--study-border)] flex-shrink-0">
                <div className="flex justify-end">
                  <button
                    onClick={closeExplanationModal}
                    className="study-button-primary px-5 py-2.5 rounded-xl transition duration-300 font-bold cursor-pointer transform hover:scale-105"
                    aria-label="Close explanation modal"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* AI Explanation Modal */}
      {showAIExplanationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="relative w-full h-full max-h-screen flex items-center justify-center p-4 sm:p-6">
            <div 
              ref={modalRef}
              className="practice-modal relative w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl rounded-xl transition-[color,background-color,border-color,opacity,transform] duration-300 ease-out flex flex-col max-h-[90vh] sm:max-h-[85vh]"
            >
              <div className="flex items-center justify-between p-4 pb-3 border-b border-[var(--study-border)] flex-shrink-0">
                <h3 className="study-title text-lg font-bold flex items-center gap-2">
                  <Lightbulb className="study-link h-5 w-5" />
                  Owl AI Explanation
                </h3>
                <button
                  onClick={closeAIModal}
                  className="study-muted hover:text-[var(--study-primary)] transition-colors cursor-pointer transform hover:scale-110"
                  aria-label="Close AI explanation modal"
                >
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto px-4 py-3">
                <div className="practice-content text-base leading-relaxed">
                  {isGeneratingAIExplanation ? (
                    <div className="flex flex-col items-center justify-center py-6">
                      <div 
                        className="w-10 h-10 border-4 border-[var(--study-border)] border-t-[var(--study-primary)] rounded-full mb-4 animate-spin"
                      ></div>
                      <p className="study-title font-bold">Generating explanation...</p>
                    </div>
                  ) : aiExplanationError ? (
                    <div className="text-red-400 text-center py-6">
                      <p className="study-muted text-sm mt-2">
                        Please Try Again 
                      </p>
                    </div>
                  ) : (
                    <div 
 
                      className="practice-content" 
 
                      dangerouslySetInnerHTML={{ __html: normalizePracticeHtml(aiExplanation) }} 
                    />
                  )}
                </div>
              </div>
              
              <div className="p-4 pt-3 border-t border-[var(--study-border)] flex-shrink-0">
                <div className="flex justify-end flex-col md:flex-row gap-3">
                  <p className="study-muted text-sm md:text-base">
                    <strong className="study-title">Note: </strong>AI might give uncertain answers sometimes - either try again or understand the fault, so please consider our explanation for more clarity.
                  </p>
                  <button
                    onClick={closeAIModal}
                    className="study-filter-idle px-5 py-2.5 rounded-xl transition duration-300 font-bold cursor-pointer transform hover:scale-105 whitespace-nowrap"
                    aria-label="Close AI explanation modal"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

